import { useDomainConfig } from "@/api/cache/appConfigQueryOptions";
import {
  useAllCarts,
  useCartCount,
  useCartUpdates,
} from "@/api/cache/cartQueryOptions";
import {
  useCurrentBrandclubProduct,
  useDtcSiteProduct,
} from "@/api/cache/dtcProductQueryOptions";
import { useUserProfile } from "@/hooks/useUserProfile";
import { createShareableShoppingCart } from "@/api/rest/checkout";
import { resolveStacklineSkuFromUrlParams } from "@/utils/bcProductMetadataUtils";
import {
  AnalyticsTrackingEvent,
  ButtonName,
  getPurchaseRewardAmountForProductV2,
  isProductEligibleForCheckout,
  ProductRewardSearchResult,
  BuyBoxDialog,
} from "@brandclub/common-ui";
import React, { useEffect, useRef, useState } from "react";
import { autoUpdateDTCProducts } from "../../api/rest/product";
import AddToBagButton, {
  AddToBagButtonProps,
} from "../../components/button/AddToBagButton";
import BagButton from "../../components/button/BagButton.tsx";
import HamburgerMenuButton from "../../components/button/HamburgerMenuButton";

import ShareProductDialog from "../../components/dialog/ShareDialog";
import DropdownMenu from "../../components/DropdownMenu";
import BarLoading from "../../components/loading/BarLoading";
import { useTrackActions } from "../../hooks/useTracking";
import { useBCRouting } from "../../providers/BCRoutingContextProvider";
import { HOST_DOMAIN } from "../../utils/domain";
import {
  formatProductReward,
  getAllProductsRewardWithRewardsCount,
} from "../../utils/misc";
import { StyledWrapper } from "../../wrappers/StyledWrapper";
import { ShopAppButton } from "@/components/button/ShopAppButton";

export interface BCCheckoutBarProps {
  style?: React.CSSProperties;
}

export const BCCheckoutBar: React.FC<BCCheckoutBarProps> = ({ style }) => {
  const bcRouting = useBCRouting();
  const [bottomOffset, setBottomOffset] = useState(0);
  const { data: domainConfig, status: appConfigStatus } = useDomainConfig();
  const { data: products, isLoading: initializingCart } = useAllCarts({
    select: (data) => data.products,
  });
  const { data: userProfile, isLoading: initializingProfile } =
    useUserProfile();
  const { data: cartCount = 0 } = useCartCount();
  const { data: currentBrandclubProduct, isLoading: productLoading } =
    useCurrentBrandclubProduct();
  const { data: dtcProduct } = useDtcSiteProduct();
  const { mutate: updateCart } = useCartUpdates();

  const [, forceUpdate] = useState<any>();

  useEffect(() => {
    const handleLocationChange = () => {
      forceUpdate({}); // Trigger re-render
    };

    // Override pushState and replaceState
    const originalPushState = bcRouting.history.pushState;
    const originalReplaceState = bcRouting.history.replaceState;

    bcRouting.history.pushState = function (...args) {
      originalPushState.apply(bcRouting.history, args);
      handleLocationChange(); // Trigger change manually
    };

    bcRouting.history.replaceState = function (...args) {
      originalReplaceState.apply(bcRouting.history, args);
      handleLocationChange(); // Trigger change manually
    };

    // Listen for popstate events (e.g., back/forward navigation)
    window.addEventListener("popstate", handleLocationChange);

    // Cleanup overrides and event listener on component unmount
    return () => {
      bcRouting.history.pushState = originalPushState;
      bcRouting.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", handleLocationChange);
    };
  }, []);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const hamburgerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let observer: MutationObserver | null = null;
    const checkForCookieBanner = () => {
      const targetNode = document.getElementById("onetrust-banner-sdk");

      if (targetNode) {
        const handleCookieBar: MutationCallback = (
          mutations: MutationRecord[]
        ) => {
          if (mutations && mutations[0] && mutations[0].target) {
            const item = mutations[0].target as HTMLElement;
            const offsets = item.getBoundingClientRect();
            setBottomOffset(offsets.height);
          }
        };

        const offsets = targetNode.getBoundingClientRect();
        setBottomOffset(offsets.height);

        observer = new MutationObserver(handleCookieBar);
        observer.observe(targetNode, {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true,
          characterDataOldValue: true,
        });

        // Clear the interval once we found the element and attached the observer
        clearInterval(intervalId);
      }
    };

    // Check for the element every 500ms
    const intervalId = setInterval(checkForCookieBanner, 500);

    // Clean up: disconnect the observer and clear the interval
    return () => {
      if (observer) observer.disconnect();
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (currentBrandclubProduct && dtcProduct)
      (async () => {
        try {
          const productToAutoUpdate: {
            stacklineSku: string;
            dtcProductPageUrl: string;
            dtcSku: string | undefined;
          }[] = [];
          const { dtcVariantId: variantId } = resolveStacklineSkuFromUrlParams(
            bcRouting.location.search
          );
          let dtcProductPageUrl = `https://${bcRouting.location.hostname}/products/${dtcProduct.handle}`;
          if (variantId) {
            dtcProductPageUrl = `https://${bcRouting.location.hostname}/products/${dtcProduct.handle}?variant=${variantId}`;
          }
          const dtcSku = !!variantId
            ? dtcProduct.variants?.find((x) => `${x.id}` === variantId)?.sku
            : dtcProduct.variants?.[0].sku;
          productToAutoUpdate.push({
            stacklineSku: currentBrandclubProduct.stacklineSku,
            dtcProductPageUrl,
            dtcSku,
          });
          if (
            currentBrandclubProduct?.matchedProducts &&
            currentBrandclubProduct?.matchedProducts?.length >= 0
          ) {
            for (const matchedProduct of currentBrandclubProduct.matchedProducts) {
              productToAutoUpdate.push({
                stacklineSku: matchedProduct.stacklineSku,
                dtcProductPageUrl,
                dtcSku: dtcProduct.variants?.[0].sku,
              });
            }
          }
          await autoUpdateDTCProducts(productToAutoUpdate);
        } catch (error) {
          console.error(error);
        }
      })();
  }, [dtcProduct, currentBrandclubProduct]);

  const handleProductPageClick = (product: ProductRewardSearchResult) => {
    bcRouting.navigate(product.retailerProductPageUrl, "_blank");
  };

  const handleBuyClick = (product?: ProductRewardSearchResult) => {
    if (!product) {
      return;
    }

    if (product?.matchedProducts?.length) {
      trackCheckoutBarAction(
        AnalyticsTrackingEvent.WEB_APP_ORGANIC_OPEN_VIEW_OFFERS,
        {
          offersTotal: product.matchedProducts.length,
        }
      );
      BuyBoxDialog.show({
        products: product.matchedProducts,
        onClick: (p) => {
          updateCart({
            action: "add",
            items: [{ ...p, quantity: 1 }],
            retailerId: p.retailerId,
          });
        },
        onProductPageClick: handleProductPageClick,
      });
    } else if (
      isProductEligibleForCheckout(
        product,
        domainConfig?.checkoutAllowedOfferType
      )
    ) {
      updateCart({
        action: "add",
        items: [{ ...product, quantity: 1 }],
        retailerId: product.retailerId,
      });
      trackCheckoutBarAction(
        AnalyticsTrackingEvent.WEB_APP_ORGANIC_ADD_TO_CART,
        {
          stacklineSku: product.stacklineSku,
          retailerId: product.retailerId,
          retailerSku: product.retailerSku,
          retailPrice: product.retailPrice,
          quantity: 1,
        }
      );
    } else {
      trackCheckoutBarAction(
        AnalyticsTrackingEvent.OPEN_RETAILER_PRODUCT_PAGE,
        {
          stacklineSku: product.stacklineSku,
          retailerId: product.retailerId,
          retailerSku: product.retailerSku,
          retailPrice: product.retailPrice,
        }
      );
      bcRouting.navigate(product.retailerProductPageUrl, "_blank");
    }
  };

  let purchaseRewardAmount = 0;
  let rewardCount = 0;
  if (currentBrandclubProduct) {
    purchaseRewardAmount = getPurchaseRewardAmountForProductV2(
      currentBrandclubProduct
    ).amount;
    rewardCount = 1;
  } else if (products) {
    [purchaseRewardAmount, rewardCount] = getAllProductsRewardWithRewardsCount(
      Object.values(products)
    );
  }

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if ((window as any).bcProductMetaData?.stacklineSku) {
      if (
        !productLoading &&
        !initializingProfile &&
        !initializingCart &&
        appConfigStatus !== "pending"
      ) {
        setLoading(false);
      }
    } else {
      if (
        !initializingProfile &&
        !initializingCart &&
        appConfigStatus !== "pending"
      ) {
        setLoading(false);
      }
    }
  }, [productLoading, initializingProfile, initializingCart, appConfigStatus]);

  const onCheckout: AddToBagButtonProps["onClick"] = async (
    _e,
    { allCarts, retailerIds }
  ) => {
    trackCheckoutBarAction(AnalyticsTrackingEvent.BC_USER_REQUESTED_CHECKOUT, {
      cartItems: allCarts.map((cart) => ({
        items: cart.items,
        retailerId: cart.retailerId,
      })),
      retailerIds,
      retailerId: retailerIds[0],
      source: "brandclub_dtc_checkout_bar",
    });
    const { shoppingCartId } = await createShareableShoppingCart({
      retailerId: allCarts[0].retailerId,
      items: allCarts[0].items,
    });
    bcRouting.location.href =
      HOST_DOMAIN +
      `/checkout?action=checkout&shareShoppingCartId=${shoppingCartId}`;
  };

  const onViewBag = async () => {
    trackCheckoutBarAction(AnalyticsTrackingEvent.VIEW_BAG, {});
    bcRouting.navigate(HOST_DOMAIN + "/mybag?action=checkout");
  };

  const barOpenOnDtc =
    !!dtcProduct ||
    (cartCount < 0 &&
      domainConfig &&
      domainConfig?.["abTestingTreatmentResults"]?.[
        "brandclub_dtc_checkout_bar"
      ]?.treatment !== "off");

  useEffect(() => {
    const body = document.querySelector("body");
    if (body) {
      if (barOpenOnDtc) {
        body.style.paddingBottom = "86px";
      } else {
        body.style.paddingBottom = "0";
      }
    }
  }, [barOpenOnDtc]);

  const isProductPage = !!currentBrandclubProduct;
  const hasItemsInCart = !!cartCount;

  const checkoutBarTreatments =
    domainConfig?.["abTestingTreatmentResults"] || {};

  const checkoutBarVisible =
    // if treatment is off, don't show the bar
    checkoutBarTreatments["brandclub_dtc_checkout_bar"]?.treatment !== "off" &&
    // AND if the it is a DTC product page or there are items in the cart show the bar
    (dtcProduct || hasItemsInCart)
      ? true
      : false;
  const [trackCheckoutBarAction, appendCheckoutBarActionDetails] =
    useTrackActions();

  if (!checkoutBarVisible) {
    return null;
  }

  return (
    <StyledWrapper
      style={{
        bottom: `${bottomOffset}px`,
        left: 0,
        zIndex: 49,
        ...style,
      }}
      className={`${"bc-animate-bcenter"}  bc-flex bc-flex-col ${"bc-fixed"} bc-w-full bc-bg-white bc-shadow-[0_-0px_10px_0px_rgba(195,188,188,0.15)] bc-z-50`}
    >
      {loading ? (
        <BarLoading />
      ) : (
        <>
          <div
            className={`bc-w-full  bc-pt-[5px] bc-pb-[15px] desktop:bc-pb-[5px] bc-box-border bc-flex bc-gap-[10px] desktop:bc-gap-[10px] bc-justify-between bc-items-center bc-px-[11px] desktop:bc-px-[24px]  bc-min-h-[74px] desktop:bc-h-[86px] bc-border-t bc-border-[#dcdddc80] bc-border-solid`}
          >
            <div
              className={`bc-items-center bc-pr-[24px] bc-max-[900px]:px-[20px]  bc-h-[86px] bc-max-[900px]:h-[64px] bc-w-full bc-z-10 bc-bottom-0 bc-border-t bc-border-l-0 bc-border-r-0 bc-border-[#dcdddc80] bc-border-solid bc-hidden desktop:!bc-flex bc-gap-[6px] desktop:bc-gap-[8px]`}
            >
              {userProfile && userProfile.given_name ? (
                <ShopAppButton
                  variant="outline"
                  buttonName={ButtonName.ProfileButton}
                  onClick={() => {
                    bcRouting.navigate(HOST_DOMAIN + "/me", "_blank");
                  }}
                >
                  Dashboard
                </ShopAppButton>
              ) : (
                <ShopAppButton
                  variant="outline"
                  buttonName={ButtonName.ProfileButton}
                  onClick={async () => {
                    bcRouting.location.href =
                      HOST_DOMAIN +
                      "/signIn?ssoRedirectUrl=" +
                      bcRouting.location.href;
                  }}
                >
                  Sign in
                </ShopAppButton>
              )}
              {/* TODO: add offers feature */}
              {currentBrandclubProduct ? (
                <ShopAppButton
                  variant="outline"
                  // Using ProfileButton styles for consistency in deprecated checkout bar
                  buttonName={ButtonName.ProfileButton}
                  onClick={() => {
                    if (!currentBrandclubProduct) {
                      return;
                    }
                    ShareProductDialog.show(currentBrandclubProduct);
                  }}
                >
                  Share
                </ShopAppButton>
              ) : null}
            </div>

            <div
              className={`bc-flex desktop:bc-hidden bc-relative bc-items-center]`}
              ref={hamburgerRef}
            >
              <div
                className="bc-w-[43px] bc-h-[43px] bc-flex bc-justify-center bc-items-center"
                onClick={() => {
                  setMobileMenuOpen(!mobileMenuOpen);
                }}
              >
                <HamburgerMenuButton />
              </div>
              {mobileMenuOpen ? (
                <div className="bc-absolute bc-bottom-[69px] bc-left-0">
                  <DropdownMenu
                    onClose={() => {
                      setMobileMenuOpen(false);
                    }}
                    containerRef={hamburgerRef}
                  >
                    <div className="bc-flex bc-flex-col bc-gap-[6px] bc-px-[8px] bc-py-[12px]">
                      {userProfile && userProfile.given_name ? (
                        <ShopAppButton
                          variant="outline"
                          buttonName={ButtonName.ProfileButton}
                          onClick={() => {
                            bcRouting.navigate(HOST_DOMAIN + "/me", "_blank");
                          }}
                        >
                          Dashboard
                        </ShopAppButton>
                      ) : (
                        <ShopAppButton
                          variant="outline"
                          buttonName={ButtonName.ProfileButton}
                          onClick={async () => {
                            bcRouting.location.href =
                              HOST_DOMAIN +
                              "/signIn?ssoRedirectUrl=" +
                              bcRouting.location.href;
                          }}
                        >
                          Sign in
                        </ShopAppButton>
                      )}
                      {currentBrandclubProduct ? (
                        <ShopAppButton
                          variant="outline"
                          // Using ProfileButton styles for consistency in deprecated checkout bar
                          buttonName={ButtonName.ProfileButton}
                          onClick={() => {
                            ShareProductDialog.show(currentBrandclubProduct);
                          }}
                        >
                          Share
                        </ShopAppButton>
                      ) : null}
                    </div>
                  </DropdownMenu>
                </div>
              ) : null}
            </div>
            <div className="!bc-flex bc-flex-1 bc-justify-end bc-gap-[6px] desktop:bc-gap-[8px] bc-items-center">
              {purchaseRewardAmount ? (
                <div
                  onClick={() => {
                    handleBuyClick(currentBrandclubProduct);
                  }}
                  className={`bc-medium-text bc-cursor-pointer bc-hidden desktop:!bc-flex bc-text-[color:var(--bc-color-primary)] bc-whitespace-nowrap bc-font-medium`}
                >
                  {formatProductReward(purchaseRewardAmount, rewardCount)}
                </div>
              ) : null}
              {hasItemsInCart && !isProductPage ? (
                <AddToBagButton onClick={onCheckout}>
                  <div className="bc-flex bc-justify-center bc-items-center">
                    Checkout
                  </div>
                </AddToBagButton>
              ) : null}
              {isProductPage ? (
                <AddToBagButton
                  price={currentBrandclubProduct?.retailPrice}
                  onClick={() => handleBuyClick(currentBrandclubProduct)}
                />
              ) : null}
            </div>
            <div
              className={`bc-flex bc-gap-[10px] desktop:bc-gap-[10px] bc-items-center`}
            >
              <BagButton onClick={onViewBag} />
            </div>
          </div>
          {purchaseRewardAmount ? (
            <div
              className={`bc-flex desktop:bc-hidden bc-justify-center bc-items-center bc-text-[color:var(--bc-color-primary)] bc-small-text bc-pb-[10px] bc-font-medium`}
            >
              {formatProductReward(purchaseRewardAmount, rewardCount)}
            </div>
          ) : null}
        </>
      )}
    </StyledWrapper>
  );
};

export default BCCheckoutBar;
